import { motion, AnimatePresence } from "framer-motion";
import { X, Navigation, ShieldAlert, ExternalLink, Clock, MapPin, RefreshCw, Flag } from "lucide-react";
import { toast } from "sonner";
import { TypeBadge, StatusBadge, ReliabilityBadge, ProposalBadge } from "@/components/Badges";
import { gmapsUrl, wazeUrl, gmapsViewUrl, formatTime, RELIABILITY, normRel } from "@/lib/events";
import { reportEnded } from "@/lib/api";

export default function EventSheet({ event, onClose }) {
  return (
    <AnimatePresence>
      {event && (
        <>
          <motion.div
            className="fixed inset-0 z-[900] bg-black/50"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            data-testid="event-sheet"
            className="fixed z-[901] bottom-0 left-0 right-0 sm:left-auto sm:right-4 sm:bottom-4 sm:w-[420px] bg-[#0B0F17] border-t sm:border border-slate-700 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[85vh] overflow-y-auto"
            initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 300 }}
          >
            <div className="sticky top-0 bg-[#0B0F17]/95 backdrop-blur px-5 pt-4 pb-3 border-b border-slate-800 flex items-start justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <TypeBadge type={event.type} />
                <StatusBadge status={event.status} />
                <ReliabilityBadge reliability={event.reliability} />
                {event.is_proposal && <ProposalBadge count={event.proposal_count} />}
              </div>
              <button data-testid="event-sheet-close" onClick={onClose} className="shrink-0 h-9 w-9 flex items-center justify-center rounded-lg bg-slate-800 text-slate-300 hover:bg-slate-700">
                <X size={18} />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <h3 className="text-lg font-bold text-slate-50 leading-snug">{event.title}</h3>
                <p className="text-sm text-slate-400 flex items-center gap-1.5 mt-1">
                  <MapPin size={14} /> {event.city}{event.department ? ` · ${event.department}` : ""}{event.department_name ? ` (${event.department_name})` : ""}
                </p>
              </div>

              {event.description && <p className="text-sm text-slate-300 leading-relaxed">{event.description}</p>}

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-[#151C28] rounded-xl p-3 border border-slate-800">
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Heure de début</p>
                  <p className="text-slate-200 flex items-center gap-1.5 mt-1"><Clock size={14} />{formatTime(event.start_time)}</p>
                </div>
                <div className="bg-[#151C28] rounded-xl p-3 border border-slate-800">
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Mise à jour</p>
                  <p className="text-slate-200 flex items-center gap-1.5 mt-1"><RefreshCw size={13} />{formatTime(event.updated_at)}</p>
                </div>
              </div>

              {event.route && (
                <div className="bg-[#151C28] rounded-xl p-3 border border-slate-800 text-sm">
                  <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">Parcours</p>
                  <p className="text-slate-200 mt-1">{event.route}</p>
                </div>
              )}

              <div className="text-xs text-slate-400 flex items-center gap-2">
                <span className="font-semibold text-slate-500">{(RELIABILITY[normRel(event.reliability)] || RELIABILITY.annonce).label} — {(RELIABILITY[normRel(event.reliability)] || RELIABILITY.annonce).desc}.</span>
              </div>

              <div className="text-xs text-slate-400">
                Source :{" "}
                {event.source_url ? (
                  <a data-testid="event-source-link" href={event.source_url} target="_blank" rel="noopener noreferrer" className="text-sky-400 hover:underline inline-flex items-center gap-1">
                    {event.source_name || event.source_url} <ExternalLink size={12} />
                  </a>
                ) : (
                  <span>{event.source_name || "—"}</span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 pt-1">
                <a data-testid={`btn-sy-rendre-${event.id}`} href={gmapsUrl(event.lat, event.lng)} target="_blank" rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-500 active:scale-95 transition text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                  <Navigation size={16} /> S'y rendre
                </a>
                <a data-testid={`btn-eviter-zone-${event.id}`} href={gmapsViewUrl(event.lat, event.lng)} target="_blank" rel="noopener noreferrer"
                  className="bg-red-600/90 hover:bg-red-500 active:scale-95 transition text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 text-sm">
                  <ShieldAlert size={16} /> Éviter cette zone
                </a>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <a href={wazeUrl(event.lat, event.lng)} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-center text-sky-400 hover:underline py-1">Ouvrir dans Waze</a>
                <span />
              </div>

              <p className="text-[11px] text-slate-500 border-t border-slate-800 pt-3">
                Vérifiez l'info trafic officielle avant de partir.
              </p>

              <button data-testid={`btn-report-ended-${event.id}`}
                onClick={async () => {
                  try {
                    const r = await reportEnded(event.id);
                    if (r.status === "termine") { toast.success("Merci — événement signalé terminé"); onClose(); }
                    else toast.success("Signalement enregistré");
                  } catch { toast.error("Signalement impossible"); }
                }}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-slate-400 hover:text-slate-200 border border-slate-800 rounded-lg py-2 transition-colors">
                <Flag size={13} /> Signaler cet événement comme terminé
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
